import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { endpoints, type SensorsData } from '@/lib/api';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Thermometer, Cpu, HardDrive } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNow } from '@/lib/hooks';

const REFETCH = 5_000;

interface ParsedCore {
  adapter: string;
  label: string;
  /** Temperature in °C, or null if unavailable. */
  tempC: number | null;
  /** Highest safe operating temp, °C. */
  maxC: number | null;
  /** Critical shutdown temp, °C. */
  critC: number | null;
}

interface ParsedAdapter {
  name: string;
  icon: 'cpu' | 'storage' | 'other';
  cores: ParsedCore[];
}

const ADAPTER_ICON: Record<string, 'cpu' | 'storage' | 'other'> = {
  coretemp: 'cpu',
  k10temp: 'cpu',
  zenpower: 'cpu',
  nvme: 'storage',
  'nvme-pci-0100': 'storage',
  drivetemp: 'storage',
  'acpi-thermal': 'other',
};

/**
 * lm-sensors 3.6.0+ (Debian bookworm) reports temperatures in °C
 * directly in the JSON output, not millidegrees. If you see 0° on
 * a hot core, you're on an older version — multiply input by 1000.
 */
function parseSensors(data: SensorsData | null): ParsedAdapter[] {
  if (!data) return [];
  const out: ParsedAdapter[] = [];

  for (const [adapterName, fields] of Object.entries(data)) {
    const cores: ParsedCore[] = [];
    for (const [label, f] of Object.entries(fields)) {
      if (typeof f !== 'object' || f === null) continue;
      const input = findNumber(f, /^temp\d+_input$/);
      const max = findNumber(f, /^temp\d+_max$/);
      const crit = findNumber(f, /^temp\d+_crit$/);
      if (input === undefined) continue;
      cores.push({
        adapter: adapterName,
        label,
        tempC: input,
        maxC: max ?? null,
        critC: crit ?? null,
      });
    }
    if (cores.length > 0) {
      out.push({
        name: adapterName,
        icon: ADAPTER_ICON[adapterName] ?? 'other',
        cores,
      });
    }
  }
  return out;
}

function findNumber(obj: Record<string, unknown>, re: RegExp): number | undefined {
  for (const [k, v] of Object.entries(obj)) {
    if (typeof v === 'number' && re.test(k)) return v;
  }
  return undefined;
}

function tempTone(degC: number | null): string {
  if (degC === null) return 'text-muted-foreground';
  if (degC < 50) return 'text-sky-400';
  if (degC < 70) return 'text-emerald-400';
  if (degC < 80) return 'text-amber-400';
  if (degC < 90) return 'text-rose-400';
  return 'text-fuchsia-400';
}

function tempBgTone(degC: number | null): string {
  if (degC === null) return 'bg-zinc-500/10 border-zinc-500/30';
  if (degC < 50) return 'bg-sky-500/5 border-sky-500/20 hover:border-sky-500/40';
  if (degC < 70) return 'bg-emerald-500/5 border-emerald-500/20 hover:border-emerald-500/40';
  if (degC < 80) return 'bg-amber-500/10 border-amber-500/30 hover:border-amber-500/50';
  if (degC < 90) return 'bg-rose-500/10 border-rose-500/30 hover:border-rose-500/50';
  return 'bg-fuchsia-500/10 border-fuchsia-500/30 hover:border-fuchsia-500/50';
}

export function SensorsWidget() {
  const now = useNow(1_000);
  const q = useQuery({
    queryKey: ['proxmox', 'sensors'],
    queryFn: endpoints.proxmox.sensors,
    refetchInterval: REFETCH,
  });

  const adapters = useMemo(() => parseSensors(q.data?.sensors ?? null), [q.data]);
  const allCores = useMemo(() => adapters.flatMap((a) => a.cores), [adapters]);

  // Hottest reading for the header
  const hottest = useMemo(() => {
    let max: ParsedCore | null = null;
    for (const c of allCores) {
      if (c.tempC === null) continue;
      if (max === null || c.tempC > (max.tempC ?? -Infinity)) max = c;
    }
    return max;
  }, [allCores]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Thermometer className="h-3.5 w-3.5" />
          Sensors
        </CardTitle>
        <div className="flex items-center gap-3 text-xs">
          {hottest && hottest.tempC !== null && (
            <span className="flex items-center gap-1">
              <span className="text-muted-foreground">max</span>
              <span className={cn('font-semibold tabular-nums', tempTone(hottest.tempC))}>
                {hottest.tempC.toFixed(0)}°
              </span>
              <span className="text-muted-foreground">on {hottest.label}</span>
            </span>
          )}
          <span className="text-muted-foreground">{allCores.length} reading(s)</span>
        </div>
      </CardHeader>
      <CardBody className="space-y-4">
        {allCores.length === 0 ? (
          <div className="rounded-md border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
            {q.isLoading
              ? 'reading sensors…'
              : q.isError
                ? 'sensor read failed'
                : 'no sensors available — install lm-sensors on the PVE host and set PVE_SSH_* env vars'}
          </div>
        ) : (
          adapters.map((adapter) => (
            <AdapterSection key={adapter.name} adapter={adapter} />
          ))
        )}

        {q.data?.lastUpdated && (
          <div className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Cpu className="h-3 w-3" />
            <span>read {relTime(q.data.lastUpdated, now)}</span>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function AdapterSection({ adapter }: { adapter: ParsedAdapter }) {
  const Icon = adapter.icon === 'cpu' ? Cpu : adapter.icon === 'storage' ? HardDrive : Thermometer;
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2 text-xs uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3 w-3" />
        <span>{adapter.name}</span>
        <span className="text-muted-foreground/60">·</span>
        <span>{adapter.cores.length} reading(s)</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {adapter.cores.map((c, i) => (
          <CoreCard key={`${c.label}-${i}`} core={c} />
        ))}
      </div>
    </div>
  );
}

function CoreCard({ core }: { core: ParsedCore }) {
  return (
    <div
      className={cn(
        'rounded-md border px-3 py-2 flex flex-col gap-0.5 transition-colors',
        tempBgTone(core.tempC),
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-muted-foreground truncate">{core.label}</span>
      </div>
      <div className="flex items-baseline gap-1">
        <span
          className={cn(
            'text-2xl font-semibold tabular-nums leading-tight',
            tempTone(core.tempC),
          )}
        >
          {core.tempC !== null ? core.tempC.toFixed(0) : '—'}
        </span>
        <span className="text-xs text-muted-foreground">°C</span>
      </div>
      {(core.maxC !== null || core.critC !== null) && (
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground/70 tabular-nums">
          {core.maxC !== null && <span>max {core.maxC.toFixed(0)}°</span>}
          {core.maxC !== null && core.critC !== null && <span> · </span>}
          {core.critC !== null && <span>crit {core.critC.toFixed(0)}°</span>}
        </div>
      )}
    </div>
  );
}

function relTime(iso: string, now: number): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '—';
  const diff = Math.max(0, Math.floor((now - t) / 1000));
  if (diff === 0) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}
